import Form from 'antd/lib/form';
import Modal from 'antd/lib/modal';
import * as React from 'react';
import Text from 'antd/lib/typography/Text';
import { useDispatch, useSelector } from 'react-redux';
import { OrgSettingsUpdatable } from '../../actions/updatable';
import Input from 'antd/lib/input';
import { RootState } from '../../reducers/reducers';
import {
    selectAllAppsIdsInOrg,
    selectOrgSettings,
    selectWebhook,
    selectApp,
    selectOrg,
    selectAppTimeFormatSetting,
} from '../../selectors/selectors';
import { OrgComponentProps } from '../../typescriptHelpers';
import TextArea from 'antd/lib/input/TextArea';
import Select from 'antd/lib/select';
import Switch from 'antd/lib/switch';
import Space from 'antd/lib/space';
import { dayjs } from '@/extendedDayjs';
import { DatetimeFormat, convertTo24HourFormat } from '../../timeHelpers';
import { UpdateWebhookRequest, AddNewWebhookRequest, tryUpdateOrgSettings } from '../../actions/orgSettings';
import { displayTextForAppPlatform } from '../../helpers/displayHelpers';
import Link from 'antd/lib/typography/Link';
import { API_DOCUMENTATION_URL } from '../../constants';
import * as AnalyticEvents from '../../utils/analytics';
import { allReleasePropertiesFrom } from '../../helpers/trackingHelpers';
import { globalMessage } from '../../utils/globalMessage';
import Button from 'antd/lib/button';
import { DeleteOutlined } from '@ant-design/icons';
import { formItemHelpStyle, runwayColors } from '@/components/styles/globalStyles';
import { useError, useIsLoading, useUpdated } from '@/utils/hooks/helpers';
import { Flex } from 'antd/lib';

interface AddUpdateWebhookModalProps extends OrgComponentProps {
    open: boolean;
    webhookId?: string;
    onClose: () => void;
    onAfterClose: () => void;
}

const AddUpdateWebhookModal: React.FC<AddUpdateWebhookModalProps> = (props: AddUpdateWebhookModalProps) => {
    const [form] = Form.useForm();
    const dispatch = useDispatch();

    const webhook = useSelector((state: RootState) => selectWebhook(state, props.orgId, props.webhookId));
    const webhookEventTypes =
        useSelector((state: RootState) => selectOrgSettings(state, props.orgId).webhookEventTypes) ?? [];

    const org = useSelector((state: RootState) => selectOrg(state, props.orgId));

    const addWebhookIsLoading = useIsLoading(OrgSettingsUpdatable.NewWebhookEndpoint);
    const addWebhookSuccess = useUpdated(OrgSettingsUpdatable.NewWebhookEndpoint);
    const addWebhookError = useError(OrgSettingsUpdatable.NewWebhookEndpoint);

    const updateWebhookIsLoading = useIsLoading(OrgSettingsUpdatable.UpdateWebhookEndpoint, props.webhookId);
    const updateWebhookSuccess = useUpdated(OrgSettingsUpdatable.UpdateWebhookEndpoint, props.webhookId);
    const updateWebhookError = useError(OrgSettingsUpdatable.UpdateWebhookEndpoint, props.webhookId);

    const redactedHeadersKeys = new Set(webhook?.redactedHeadersKeys ?? []);

    const appOptions = useSelector((state: RootState) => {
        const appIdsForOrg = selectAllAppsIdsInOrg(state, props.orgId);

        return appIdsForOrg.map((appId) => {
            const app = selectApp(state, appId);

            return {
                appId: app.appId,
                appName: app.appSettings.appName,
                platform: app.appSettings.platform,
                appStoreType: app.appSettings.appStoreType,
            };
        });
    });

    const use24HourTimeFormat = useSelector((state: RootState) => {
        const appIdsForOrg = selectAllAppsIdsInOrg(state, props.orgId);
        if (appIdsForOrg.length === 0) return false;

        const timeFormatSettings = appIdsForOrg.map((appId) => selectAppTimeFormatSetting(state, appId));
        return timeFormatSettings.every((setting) => setting === timeFormatSettings[0]) ? timeFormatSettings[0] : false;
    });

    const dateFormat = DatetimeFormat.DayAbbreviatedMonthDateYearTimeAbbreviated;

    const onSubmit = (form) => {
        form.validateFields()
            .then((values) => {
                let request: UpdateWebhookRequest | AddNewWebhookRequest = null;
                let action = null;

                const customHeaders: Record<string, string | null> = {};

                // Determining headers to add/update
                values.customHeaders?.map((header) => {
                    if (header.headerValue) {
                        customHeaders[header.headerKey] = header.headerValue;
                    }
                });

                // Determining redacted headers to remove
                redactedHeadersKeys.forEach((headerKey) => {
                    // If no longer present, set it as null to be deleted on the backend
                    if (!values.customHeaders?.find((header) => header.headerKey === headerKey)) {
                        customHeaders[headerKey] = null;
                    }
                });

                if (props.webhookId) {
                    request = {
                        orgId: props.orgId,
                        webhookId: props.webhookId,
                        eventTypes: values.eventTypes ?? [],
                        ...values,
                        customHeaders,
                    };
                    action = OrgSettingsUpdatable.UpdateWebhookEndpoint;
                } else {
                    request = {
                        orgId: props.orgId,
                        eventTypes: values.eventTypes ?? [],
                        ...values,
                        customHeaders,
                    };
                    action = OrgSettingsUpdatable.NewWebhookEndpoint;
                }

                dispatch(tryUpdateOrgSettings(action, request, props.webhookId));
            })
            .then(() => {
                const properties = allReleasePropertiesFrom(org);

                AnalyticEvents.track(AnalyticEvents.Event.AddWebhookEndpoint, properties);
            })
            .catch((error) => {
                globalMessage.error(error.message);
            });
    };

    React.useEffect(() => {
        if (updateWebhookSuccess) {
            globalMessage.success('Updated webhook!');

            props.onClose();
        }
    }, [updateWebhookSuccess]);

    React.useEffect(() => {
        if (addWebhookSuccess) {
            globalMessage.success('Added new webhook!');

            props.onClose();
        }
    }, [addWebhookSuccess]);

    React.useEffect(() => {
        if (updateWebhookError) {
            globalMessage.error(updateWebhookError);
        }
    }, [updateWebhookError]);

    React.useEffect(() => {
        if (addWebhookError) {
            globalMessage.error(addWebhookError);
        }
    }, [addWebhookError]);

    React.useEffect(() => {
        if (!props.open) {
            form.resetFields();
        }
    }, [form, props.open]);

    const modalTitle = props.webhookId ? 'Edit webhook endpoint' : 'Add webhook endpoint';
    const ctaText = props.webhookId ? 'Update' : 'Add';

    const formInitialValues = React.useMemo(() => {
        if (!webhook) {
            return {};
        }

        const customHeaders = Object.keys(webhook.customHeaders).map((headerKey) => ({
            headerKey,
            headerValue: webhook.customHeaders[headerKey],
        }));

        for (const redactedHeaderKey of redactedHeadersKeys) {
            customHeaders.push({
                headerKey: redactedHeaderKey,
                headerValue: '',
            });
        }

        return {
            url: webhook.url,
            description: webhook.description,
            eventTypes: webhook.eventTypes,
            appIds: webhook.appIds,
            enabled: webhook.enabled,
            customHeaders,
        };
    }, [webhook, redactedHeadersKeys]);

    return (
        <Modal
            open={props.open}
            destroyOnClose
            title={
                <div style={{ paddingRight: 24 }}>
                    <Text>{modalTitle}</Text>
                </div>
            }
            onCancel={props.onClose}
            okText={ctaText}
            okButtonProps={{ loading: updateWebhookIsLoading || addWebhookIsLoading }}
            onOk={() => form.submit()}
            afterClose={() => {
                form.resetFields();
                props.onAfterClose();
            }}
            focusTriggerAfterClose={false}
        >
            <div style={{ display: 'flex', flexDirection: 'column' }}>
                <Form
                    form={form}
                    preserve={false}
                    initialValues={formInitialValues}
                    onFinish={() => onSubmit(form)}
                    layout="vertical"
                >
                    <Form.Item name={'url'} label="Endpoint" rules={[{ required: true }]} style={{ marginBottom: 12 }}>
                        <Input placeholder="https://api.your-app.com/webhookHandler" />
                    </Form.Item>
                    <Form.Item name={'description'} label="Description" style={{ marginBottom: 12 }}>
                        <TextArea rows={3} placeholder="A description of the webhook" />
                    </Form.Item>
                    <Form.Item
                        name="eventTypes"
                        label="Event types"
                        help={
                            <Text type="secondary" style={formItemHelpStyle}>
                                If no event types are selected, the endpoint will receive all events. For more details
                                on each event type and to view event payloads, visit the{' '}
                                <Link href={API_DOCUMENTATION_URL} target="_blank" style={{ fontSize: 12 }}>
                                    API documentation
                                </Link>
                                .
                            </Text>
                        }
                    >
                        <Select mode="multiple" placeholder="Select" optionLabelProp="label">
                            {webhookEventTypes.map((eventType, index) => {
                                return (
                                    <Select.Option key={index} value={eventType.type} label={eventType.type}>
                                        <Space direction="vertical" size={4}>
                                            <Text>{eventType.type}</Text>
                                            <Text type="secondary" ellipsis style={{ fontSize: 12 }}>
                                                {eventType.description}
                                            </Text>
                                        </Space>
                                    </Select.Option>
                                );
                            })}
                        </Select>
                    </Form.Item>
                    <Form.Item
                        name="appIds"
                        label="Apps"
                        help={
                            <Text type="secondary" style={formItemHelpStyle}>
                                Select the apps this endpoint will be notified about. Leave blank to receive messages
                                from all apps.
                            </Text>
                        }
                    >
                        <Select placeholder="Select apps" mode="multiple">
                            {appOptions.map((appOption) => {
                                return (
                                    <Select.Option value={appOption.appId} key={appOption.appId}>
                                        {appOption.appName}{' '}
                                        <Text type="secondary">
                                            ({displayTextForAppPlatform(appOption.platform, appOption.appStoreType)})
                                        </Text>
                                    </Select.Option>
                                );
                            })}
                        </Select>
                    </Form.Item>
                    {props.webhookId && (
                        <Form.Item name="enabled" label="Enabled" valuePropName="checked" style={{ marginBottom: 12 }}>
                            <Switch />
                        </Form.Item>
                    )}
                    <Form.List name="customHeaders">
                        {(fields, { add, remove }) => {
                            return (
                                <Space direction="vertical">
                                    <Text>Custom headers</Text>
                                    <Space direction="vertical">
                                        {fields.map((field) => {
                                            const customHeaders = form.getFieldValue('customHeaders');
                                            const headerKey = customHeaders[field.name]?.headerKey;
                                            const isRedacted = redactedHeadersKeys.has(headerKey);
                                            return (
                                                <Flex key={field.key}>
                                                    <Space direction="horizontal" align="start">
                                                        <Form.Item
                                                            {...field}
                                                            rules={[{ required: true, message: 'Key is required' }]}
                                                            name={[field.name, 'headerKey']}
                                                            style={{ marginBottom: 0 }}
                                                        >
                                                            <Input placeholder="Authorization" />
                                                        </Form.Item>
                                                        <Form.Item
                                                            {...field}
                                                            rules={[
                                                                { required: !isRedacted, message: 'Value is required' },
                                                            ]}
                                                            name={[field.name, 'headerValue']}
                                                            style={{ marginBottom: 0 }}
                                                        >
                                                            <Input
                                                                className={isRedacted ? 'redacted-input' : ''}
                                                                placeholder={
                                                                    isRedacted ? '••••••••••••' : 'Bearer xxxx'
                                                                }
                                                            />
                                                        </Form.Item>
                                                    </Space>
                                                    <Button
                                                        icon={
                                                            <DeleteOutlined style={{ color: runwayColors.statusRed }} />
                                                        }
                                                        danger
                                                        type="link"
                                                        onClick={() => remove(field.name)}
                                                    />
                                                </Flex>
                                            );
                                        })}
                                    </Space>
                                    <Button onClick={() => add()}>Add custom header</Button>
                                    {redactedHeadersKeys.size > 0 && (
                                        <Text type="secondary" style={formItemHelpStyle}>
                                            Sensitive values (like API keys) are automatically redacted for security. To
                                            update these headers, please re-enter the full value.
                                        </Text>
                                    )}
                                </Space>
                            );
                        }}
                    </Form.List>
                </Form>
                {webhook?.createdAt && (
                    <Text type="secondary" style={{ marginTop: 12 }}>
                        Created:{' '}
                        {dayjs(webhook.createdAt).format(
                            use24HourTimeFormat ? convertTo24HourFormat(dateFormat) : dateFormat,
                        )}
                    </Text>
                )}
            </div>
        </Modal>
    );
};

export default AddUpdateWebhookModal;
